import { Box, Card, Stack, Typography, Button } from "@mui/material";
import { Firma, FirmaFields, FirmaLabel, FirmaPutFields } from "../../types/firma";
import { FirmasProps } from "../SeccionFirmas";
import CustomChip from 'src/@core/components/mui/chip'
import { dateFormatter } from "src/utils/formatters";
import { z } from "zod";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { userStorage } from "src/utils/localStorage";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { BaseResponseFields, EntityWithIdFields } from "src/types";
import { HttpSistema } from "../../api/httpSistema";
import { ControlledTextField } from "src/components/forms";

const schemaActualizarFirma = z.object({
    observaciones: z.string().optional(),   // TODO: maxlength
    // codFirmaResultado: z.number(),
    mailUsuario: z.string().email()
})

type SchemaActualizarFirma = z.infer<typeof schemaActualizarFirma>

type FirmaParaFirmarProps = {
    idOperacion: number,
    codSistema: number,
    firma: Firma,
    reload: () => void,
    labels: FirmaLabel[]
}

const FirmaFirmar = ({ idOperacion, codSistema, firma, reload, labels }: FirmaParaFirmarProps) => {

    // Id del label que se clickeó para firmar, para deshabilitar los otros botones.
    const [firmando, setFirmando] = useState<number>(0);

    const { codFirmaResultado, etiqueta, cargo, observaciones, apellidoYNombre, mailUsuarioFirma, fechaFirma } = firma;

    const user = userStorage.get()

    const formMethods = useForm<SchemaActualizarFirma>({
        defaultValues: {
            observaciones: observaciones ?? ''
        }
    })

    const navigation = useNavigate()

    const onSubmitWrapper = (type: number) => {
        return new Promise<void>((resolve, reject) => {
            formMethods.handleSubmit(
                (formData) => {
                    toast.promise(handleFirmar(formData, type), {
                        loading: 'Firmando',
                        success: type === 1 ? 'Aprobado correctamente' :
                            type === 2 ? 'Rechazado correctamente' : 'Firmado correctamente.',
                        error: (e) => `Error: ${e}`
                    })
                        .then(() => {
                            setTimeout(() => navigation('/'), 500)
                            resolve()
                        })
                        .catch((err) => {
                            reject(err)
                            setFirmando(0)
                        })
                },
                (validationErrors) => {
                    reject(validationErrors)
                    console.error(validationErrors)
                    setFirmando(0)
                }
            )();
        })
    }

    const handleFirmar = (data: SchemaActualizarFirma, type: number) => {
        const firmaUpdate = {
            [FirmaPutFields.CodFirmaResultado]: type,
            [FirmaPutFields.CodUsuarioFirma]: user.userId,
            [FirmaPutFields.FirmaUsuario]: '',
            [FirmaPutFields.MailUsuario]: user.mail,
            [FirmaPutFields.Observaciones]: data.observaciones
        }

        return new Promise<void>((resolve, reject) => {
            HttpSistema.putFirma(idOperacion, codSistema, firma[EntityWithIdFields.Id], firmaUpdate)
                .then((res) => {
                    if (res?.[BaseResponseFields.HasError]) {
                        reject(res?.[BaseResponseFields.ErrorDescription] || '')
                    }
                    resolve(res)
                })
                .catch((err) => {
                    reject(err?.[BaseResponseFields.ErrorDescription] || '')
                })
        })
    }

    return (
        <Card sx={{ height: '100%', p: 2 }}>
            <Stack spacing={2}>
                <Box display='flex' justifyContent='space-between'>
                    <Stack>
                        <Box>
                            <CustomChip size='small' color='info' label={cargo} sx={{
                                fontSize: 11,
                                height: 15
                            }} />
                        </Box>
                    </Stack>
                </Box>
                <Stack spacing={2}>
                    <ControlledTextField
                        label='Observaciones'
                        name='observaciones'
                        multiline
                        rows={2}
                        fullWidth
                        control={formMethods.control}
                    />
                    <Stack direction='row-reverse' display='flex' justifyContent='right' spacing={2}>
                        {
                            labels.map(label => (
                                <Button
                                    key={label.id}
                                    variant='contained'
                                    color={
                                        label.id === 1 ? 'success' : label.id === 2 ? 'error' : 'primary'
                                    }
                                    onClick={() => {
                                        setFirmando(label.id)

                                        return onSubmitWrapper(label.id)
                                    }}
                                    disabled={(firmando !== 0)}
                                    size='small'
                                >
                                    {label.firmaResultadoDescripcion}
                                </Button>
                            ))
                        }
                    </Stack>
                </Stack>
            </Stack>
        </Card >
    )
}

const FirmaFija = ({ firma }: { firma: Firma }) => {

    const { fechaFirma, mailUsuarioFirma, apellidoYNombre, observaciones, etiqueta, cargo, codFirmaResultado } = firma;

    return (
        <Card sx={{ height: '100%', p: 2 }}>
            <Stack spacing={2}>
                <Box display='flex' justifyContent='space-between'>
                    <Stack>
                        <Box>
                            <CustomChip size='small' color='info' label={cargo} sx={{
                                fontSize: 11,
                                height: 15
                            }} />
                        </Box>
                        <Typography>
                            {codFirmaResultado === 0 ? 'Pendiente de firma' : (apellidoYNombre || '')}
                        </Typography>
                        <Typography variant='body2'>
                            {codFirmaResultado === 0 ? 'Usted no tiene permisos para firmar.' : (mailUsuarioFirma || '')}
                        </Typography>
                    </Stack>
                    <Stack spacing={1}>
                        {
                            codFirmaResultado == 0 ? <CustomChip label='Pendiente' size='small' skin='light' sx={{
                                fontSize: 11,
                                height: 15
                            }} /> :
                                codFirmaResultado == 1 ? <CustomChip label='Aprobado' color='success' size='small' skin='light' sx={{
                                    fontSize: 11,
                                    height: 15
                                }} /> :
                                    codFirmaResultado == 2 ? <CustomChip label='Rechazado' color='error' size='small' skin='light' sx={{
                                        fontSize: 11,
                                        height: 15
                                    }} /> : <></>
                        }
                        {
                            fechaFirma && <Typography align='right' variant='caption'>{dateFormatter.toShortDate(fechaFirma)}</Typography>
                        }
                    </Stack>
                </Box>
                {
                    observaciones && <Typography variant='body2'>{observaciones}</Typography>
                }
            </Stack>
        </Card>
    )
}

export const FirmasMobile = ({ firmas, idOperacion, codSistema, refetch }: FirmasProps) => {

    return (
        <Stack spacing={1}>
            {
                firmas.firmas.map(firma => {
                    const labels = firmas.labels.filter(label => label.intIdOperacionFirma === firma.id)

                    let componente;

                    if (firma[FirmaFields.PermiteFirmar]) {
                        componente = <FirmaFirmar
                            idOperacion={idOperacion}
                            firma={firma}
                            reload={refetch}
                            labels={labels}
                            codSistema={codSistema}
                        />
                    } else {
                        componente = <FirmaFija firma={firma} />
                    }

                    return <Box key={firma.id}>{componente}</Box>
                })
            }
        </Stack>
    )
}
